import React, { useState, useEffect, useCallback } from 'react';
import { PuzzleService, formatPuzzleDisplayTitle, type Puzzle, type PuzzleFilters } from '../../services/puzzleService';
import './PuzzleList.css';

interface PuzzleListProps {
  onPuzzleSelect: (puzzleId: number, puzzleIds: number[]) => void;
  authState?: {
    isAuthenticated: boolean;
    user: {
      id: string;
      username: string;
      email: string;
    } | null;
  };
}

const PuzzleList: React.FC<PuzzleListProps> = ({ onPuzzleSelect, authState }) => {
  const [puzzles, setPuzzles] = useState<Puzzle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<PuzzleFilters>({
    limit: 500,
    offset: 0,
    orderBy: 'catalog_number',
    orderDir: 'asc',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = filters.limit || 500;

  const loadPuzzles = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await PuzzleService.getPuzzles(filters);
      
      if (result.success && result.data) {
        setPuzzles(result.data.puzzles);
        setTotalCount(result.data.count);
      } else {
        setError(result.error || 'Failed to load puzzles');
      }
    } catch (err) {
      setError('An error occurred while loading puzzles');
      console.error('Error loading puzzles:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadPuzzles();
  }, [loadPuzzles]);

  const handleDifficultyFilter = (difficulty: 'easy' | 'medium' | 'hard' | 'expert' | 'insane' | 'all') => {
    setCurrentPage(1);
    setFilters(prev => ({
      ...prev,
      difficulty: difficulty === 'all' ? undefined : difficulty,
      offset: 0,
    }));
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setFilters(prev => ({
      ...prev,
      offset: (page - 1) * (prev.limit || 500),
    }));
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return '#4CAF50';
      case 'medium':
        return '#FF9800';
      case 'hard':
        return '#F44336';
      case 'expert':
        return '#9C27B0';
      case 'insane':
        return '#E91E63';
      default:
        return '#999';
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <div className="puzzle-list">
      <div className="puzzle-list-header">
        <h2>Browse Puzzles</h2>
        <div className="puzzle-list-filters">
          <button
            className={`filter-btn ${!filters.difficulty ? 'active' : ''}`}
            onClick={() => handleDifficultyFilter('all')}
          >
            All
          </button>
          <button
            className={`filter-btn ${filters.difficulty === 'easy' ? 'active' : ''}`}
            onClick={() => handleDifficultyFilter('easy')}
          >
            Easy
          </button>
          <button
            className={`filter-btn ${filters.difficulty === 'medium' ? 'active' : ''}`}
            onClick={() => handleDifficultyFilter('medium')}
          >
            Medium
          </button>
          <button
            className={`filter-btn ${filters.difficulty === 'hard' ? 'active' : ''}`}
            onClick={() => handleDifficultyFilter('hard')}
          >
            Hard
          </button>
          <button
            className={`filter-btn ${filters.difficulty === 'expert' ? 'active' : ''}`}
            onClick={() => handleDifficultyFilter('expert')}
          >
            Expert
          </button>
          <button
            className={`filter-btn ${filters.difficulty === 'insane' ? 'active' : ''}`}
            onClick={() => handleDifficultyFilter('insane')}
          >
            Insane
          </button>
        </div>
        {!loading && !error && totalCount > 0 && (
          <div className="puzzle-list-count">
            {totalCount} puzzle{totalCount === 1 ? '' : 's'}
          </div>
        )}
      </div>

      {loading && (
        <div className="puzzle-list-loading">
          <p>Loading puzzles...</p>
        </div>
      )}

      {error && (
        <div className="puzzle-list-error">
          <p>Error: {error}</p>
          <button onClick={loadPuzzles}>Retry</button>
        </div>
      )}

      {!loading && !error && puzzles.length === 0 && (
        <div className="puzzle-list-empty">
          <p>No puzzles found. Be the first to create one!</p>
        </div>
      )}

      {!loading && !error && puzzles.length > 0 && (
        <>
          <div className="puzzle-list-grid">
            {puzzles.map((puzzle) => (
              <div
                key={puzzle.id}
                className="puzzle-card"
                onClick={() => onPuzzleSelect(puzzle.id, puzzles.map((item) => item.id))}
              >
                <div className="puzzle-card-header">
                  <h3 className="puzzle-card-title">{formatPuzzleDisplayTitle(puzzle)}</h3>
                  <span
                    className="puzzle-card-difficulty"
                    style={{ color: getDifficultyColor(puzzle.difficulty) }}
                  >
                    {puzzle.difficulty}
                  </span>
                </div>
                {puzzle.description && (
                  <p className="puzzle-card-description">{puzzle.description}</p>
                )}
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="puzzle-list-pagination">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="pagination-btn"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PuzzleList;







